/**
 * ConfirmProvider — promise-based host for ConfirmDialog (BACKLOG-1297).
 *
 * Mounted once at the app root (main.tsx) so any surface can ask a question
 * without threading modal state through its parents:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Discard?', tone: 'danger' }))) return;
 *
 *   const reason = await prompt({ title: 'Delete', input: { label: 'Reason' } });
 *   if (reason === null) return;           // null === cancelled, like window.prompt
 *
 *   await alert({ title: 'Delete failed', message: err });
 *
 * One dialog is live at a time. A second request while one is open supersedes
 * it and settles the older promise as cancelled, so no caller is ever left
 * awaiting a dialog nobody can see.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  ConfirmDialog,
  type ConfirmOptions,
  type ConfirmOutcome,
  type PromptOptions,
} from './ConfirmDialog';

/** Acknowledge-only options — no cancel button, so no tone/input/labels for it. */
export type AlertOptions = Omit<ConfirmOptions, 'acknowledgeOnly' | 'cancelLabel' | 'input'>;

interface DialogHost {
  request: (options: ConfirmOptions) => Promise<ConfirmOutcome>;
}

interface PendingRequest {
  id: number;
  options: ConfirmOptions;
  settle: (outcome: ConfirmOutcome) => void;
}

const DialogHostContext = createContext<DialogHost | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }): JSX.Element {
  const [pending, setPending] = useState<PendingRequest | null>(null);
  // Mirror of `pending` so request/close can read + settle the live request
  // without waiting for a render (two asks in the same tick are legal).
  const pendingRef = useRef<PendingRequest | null>(null);
  const nextId = useRef(0);

  const request = useCallback(
    (options: ConfirmOptions): Promise<ConfirmOutcome> =>
      new Promise<ConfirmOutcome>((resolve) => {
        pendingRef.current?.settle({ confirmed: false, value: '' });
        nextId.current += 1;
        const next: PendingRequest = { id: nextId.current, options, settle: resolve };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  const resolveCurrent = useCallback((outcome: ConfirmOutcome): void => {
    const current = pendingRef.current;
    if (!current) return;
    pendingRef.current = null;
    setPending(null);
    current.settle(outcome);
  }, []);

  const host = useMemo<DialogHost>(() => ({ request }), [request]);

  return (
    <DialogHostContext.Provider value={host}>
      {children}
      {pending && (
        // Keyed by request id — each ask gets a fresh ModalShell (focus,
        // field value, scroll lock) even when one dialog replaces another.
        <ConfirmDialog
          key={pending.id}
          open
          {...pending.options}
          onResolve={resolveCurrent}
        />
      )}
    </DialogHostContext.Provider>
  );
}

function useDialogHost(): DialogHost {
  const ctx = useContext(DialogHostContext);
  if (!ctx) throw new Error('useConfirm/usePrompt/useAlert must be used inside <ConfirmProvider/>');
  return ctx;
}

/** Ask a yes/no question. Resolves true when the operator commits. */
export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const { request } = useDialogHost();
  return useCallback(
    async (options: ConfirmOptions): Promise<boolean> => (await request(options)).confirmed,
    [request],
  );
}

/** Ask for a value. Resolves the trimmed text, or null when cancelled. */
export function usePrompt(): (options: PromptOptions) => Promise<string | null> {
  const { request } = useDialogHost();
  return useCallback(
    async (options: PromptOptions): Promise<string | null> => {
      const outcome = await request(options);
      return outcome.confirmed ? outcome.value : null;
    },
    [request],
  );
}

/** Tell the operator something. Resolves once they acknowledge it. */
export function useAlert(): (options: AlertOptions) => Promise<void> {
  const { request } = useDialogHost();
  return useCallback(
    async (options: AlertOptions): Promise<void> => {
      await request({ ...options, acknowledgeOnly: true });
    },
    [request],
  );
}
